import React, { useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import './QuickTable.scss';

import { useTableSort, useTableSearch, useColumnVisibility, useTableData } from './hooks';
import { TableHeader, TableRow } from './components';

const QuickTable = ({
  id = 'query',
  title,
  columns = [],
  data = [],
  loading = false,
  order,
  reverse = false,
  clickRowHandler,
  sortCB,
  fixedSize = false,
  largeHeader = false,
  searchPosition = 0,
  excel = false,
  exportFn,
  maxMin = false,
  crud = false,
  crudTemplateHead,
  crudTemplateBody,
  onEdit,
  onDelete,
  className = '',
  onRowClick,
  disabledIds = {},
  emphasisedIds = {},
  selectedRow,
}) => {
  const tableRef = useRef(null);
  const [isMaxed, setIsMaxed] = useState(false);

  const tableTitle = useMemo(() => {
    const t = title || (columns[0] && columns[0].title) || '';
    return t.endsWith('s') ? t : t + 's';
  }, [title, columns]);

  // Hooks
  const { sortConfig, handleClickColumn } = useTableSort({ order, reverse, columns, sortCB });

  const { search, searchOpened, searchInputRef, handleToggleSearch, handleClearSearch, handleSearchChange } =
    useTableSearch({ tableTitle });

  const {
    columnsState,
    visibleColumns,
    allColumns,
    columnSelectionOpened,
    setColumnSelectionOpened,
    setColumnSelectionHovered,
    handleSelectColumn,
    handleSelectAllColumns,
    handleLeavePencil,
  } = useColumnVisibility({ columns, tableTitle });

  const { dataArray, viewArray } = useTableData({ data, search, sortConfig, columnsState });

  const handleRowClick = useCallback(
    (row) => {
      if (clickRowHandler) clickRowHandler(row);
      if (onRowClick) onRowClick(row);
    },
    [clickRowHandler, onRowClick]
  );

  const handleResize = useCallback(() => setIsMaxed((prev) => !prev), []);
  const handleExport = useCallback(() => exportFn && exportFn(), [exportFn]);

  return (
    <div className={classNames('quick-table-wrap', className)}>
      <div
        ref={tableRef}
        className={classNames('quick-table', { 'fixed-size': fixedSize, 'large-header': largeHeader })}
      >
        <TableHeader
          visibleColumns={visibleColumns}
          sortConfig={sortConfig}
          handleClickColumn={handleClickColumn}
          searchPosition={searchPosition}
          search={search}
          searchOpened={searchOpened}
          searchInputRef={searchInputRef}
          handleToggleSearch={handleToggleSearch}
          handleSearchChange={handleSearchChange}
          handleClearSearch={handleClearSearch}
          loading={loading}
          viewArrayLength={viewArray.length}
          columnsState={columnsState}
          allColumns={allColumns}
          columnSelectionOpened={columnSelectionOpened}
          setColumnSelectionOpened={setColumnSelectionOpened}
          setColumnSelectionHovered={setColumnSelectionHovered}
          handleSelectColumn={handleSelectColumn}
          handleSelectAllColumns={handleSelectAllColumns}
          handleLeavePencil={handleLeavePencil}
          fixedSize={fixedSize}
          excel={excel}
          exportFn={exportFn}
          handleExport={handleExport}
          id={id}
          tableTitle={tableTitle}
          maxMin={maxMin}
          isMaxed={isMaxed}
          handleResize={handleResize}
          crud={crud}
          crudTemplateHead={crudTemplateHead}
        />

        <div className={classNames('tbody', { loading })} onClick={(e) => e.stopPropagation()}>
          {viewArray.map((row, rowIndex) => (
            <TableRow
              key={row.id || rowIndex}
              row={row}
              rowIndex={rowIndex}
              visibleColumns={visibleColumns}
              selectedRow={selectedRow}
              disabledIds={disabledIds}
              emphasisedIds={emphasisedIds}
              tableTitle={tableTitle}
              handleRowClick={handleRowClick}
              searchPosition={searchPosition}
              crud={crud}
              crudTemplateBody={crudTemplateBody}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>

        {loading && <div className="loading-img"></div>}

        {!loading && viewArray.length === 0 && (
          <div className="loading-img no-data">
            <span>{dataArray.length === 0 ? `No ${tableTitle} found.` : `No ${tableTitle} matching your filter.`}</span>
          </div>
        )}
      </div>
    </div>
  );
};

QuickTable.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
      ellipsis: PropTypes.bool,
      template: PropTypes.string,
      templateHead: PropTypes.string,
      flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      hidden: PropTypes.bool,
      alwaysShown: PropTypes.bool,
      alwaysHidden: PropTypes.bool,
      sort: PropTypes.bool,
      search: PropTypes.bool,
      sym: PropTypes.string,
      nonFormatable: PropTypes.bool,
      displayValue: PropTypes.string,
      lockable: PropTypes.bool,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  order: PropTypes.string,
  reverse: PropTypes.bool,
  clickRowHandler: PropTypes.func,
  sortCB: PropTypes.func,
  fixedSize: PropTypes.bool,
  largeHeader: PropTypes.bool,
  searchPosition: PropTypes.number,
  excel: PropTypes.bool,
  exportFn: PropTypes.func,
  maxMin: PropTypes.bool,
  crud: PropTypes.bool,
  crudTemplateHead: PropTypes.string,
  crudTemplateBody: PropTypes.string,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  className: PropTypes.string,
  onRowClick: PropTypes.func,
  disabledIds: PropTypes.object,
  emphasisedIds: PropTypes.object,
  selectedRow: PropTypes.object,
};

export default QuickTable;
